import { useCallback, useEffect, useRef, useState } from 'react'

import { listScriptDocumentComments, type ScriptComment } from '@/api/studio-production'
import { listWriterScriptDocumentComments } from '@/api/studio-writer'

const DEFAULT_PAGE_SIZE = 30

export function useScriptDocumentComments(opts: {
  productionId?: string
  scriptId: number
  writer?: boolean
  enabled?: boolean
  pageSize?: number
}) {
  const { productionId, scriptId, writer = false, enabled = true, pageSize = DEFAULT_PAGE_SIZE } = opts
  const [comments, setComments] = useState<ScriptComment[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const offsetRef = useRef(0)

  const fetchPage = useCallback(
    async (offset: number, append: boolean) => {
      if (!enabled) return
      if (writer) {
        const res = await listWriterScriptDocumentComments(scriptId, { limit: pageSize, offset })
        setTotal(res.total ?? res.items.length)
        setComments((prev) => (append ? [...prev, ...res.items] : res.items))
        offsetRef.current = offset + res.items.length
        return
      }
      if (!productionId) return
      const res = await listScriptDocumentComments(productionId, scriptId, { limit: pageSize, offset })
      setTotal(res.total ?? res.items.length)
      setComments((prev) => (append ? [...prev, ...res.items] : res.items))
      offsetRef.current = offset + res.items.length
    },
    [enabled, writer, productionId, scriptId, pageSize],
  )

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      offsetRef.current = 0
      await fetchPage(0, false)
    } finally {
      setLoading(false)
    }
  }, [fetchPage])

  const loadMore = useCallback(async () => {
    if (loadingMore || comments.length >= total) return
    setLoadingMore(true)
    try {
      await fetchPage(offsetRef.current, true)
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, comments.length, total, fetchPage])

  useEffect(() => {
    void reload()
  }, [reload])

  return {
    comments,
    total,
    loading,
    loadingMore,
    reload,
    loadMore,
    hasMore: comments.length < total,
  }
}
